import { useState, useCallback } from 'react';
import type { Id } from '../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: hooks in src/hooks/ need convex Id type from @types

type TDepth = '1' | '2' | '3' | '4' | '5';

interface IWebsiteCrawlFormState {
  url: string;
  depth: TDepth;
  isShared: boolean;
  isCrawling: boolean;
  selectedIds: Set<Id<'knowledgeBaseDocuments'>>;
}

const INITIAL_STATE: IWebsiteCrawlFormState = {
  url: '',
  depth: '3',
  isShared: false,
  isCrawling: false,
  selectedIds: new Set(),
};

export function useWebsiteCrawlForm() {
  const [state, setState] = useState<IWebsiteCrawlFormState>(INITIAL_STATE);

  const setUrl = useCallback((url: string) => {
    setState(prev => ({ ...prev, url }));
  }, []);

  const setDepth = useCallback((depth: TDepth) => {
    setState(prev => ({ ...prev, depth }));
  }, []);

  const setIsShared = useCallback((isShared: boolean) => {
    setState(prev => ({ ...prev, isShared }));
  }, []);

  const setIsCrawling = useCallback((isCrawling: boolean) => {
    setState(prev => ({ ...prev, isCrawling }));
  }, []);

  const isValidUrl = useCallback((value: string): boolean => {
    try {
      const parsed = new URL(value);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }, []);

  const toggleSelect = useCallback((id: Id<'knowledgeBaseDocuments'>) => {
    setState(prev => {
      const next = new Set(prev.selectedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return { ...prev, selectedIds: next };
    });
  }, []);

  const selectAll = useCallback((ids: Id<'knowledgeBaseDocuments'>[]) => {
    setState(prev => {
      const allSelected = ids.every(id => prev.selectedIds.has(id));
      return {
        ...prev,
        selectedIds: allSelected ? new Set() : new Set(ids),
      };
    });
  }, []);

  const resetForm = useCallback(() => {
    setState(prev => ({
      ...prev,
      url: '',
      depth: '3',
      isShared: false,
      isCrawling: false,
    }));
  }, []);

  return {
    state,
    setUrl,
    setDepth,
    setIsShared,
    setIsCrawling,
    isValidUrl,
    toggleSelect,
    selectAll,
    resetForm,
  };
}
